import type { Task } from '../services/taskService';
import type { SupervisorButton } from '../types/supervisorButtons';

import { orderedPlanningTasks } from './planningTaskPresentation';

const STAGES = {
  srd: 'Generate system requirement document',
  user_flows: 'Generate personas & user flows',
  design_first_page: 'Design first page',
  design_remaining: 'Design remaining pages',
  architecture: 'Create architecture',
  pm: 'Create workspace task plan',
  artifact_updates: 'Update dependent artifacts',
};

type Stage = keyof typeof STAGES;
const stageOrder = Object.keys(STAGES) as Stage[];
const phaseStages: Record<string, Stage[]> = {
  user_flow: ['design_first_page'], first_page: ['design_remaining'],
  all_pages: ['architecture', 'pm'], review_build: ['pm'],
  artifact_updates: ['artifact_updates'],
};
const labelStages: Record<string, Stage[]> = {
  'Generate User Flows': ['user_flows'],
  'Generate First Page': ['design_first_page'], 'Retry First Page': ['design_first_page'],
  'Generate All Pages': ['design_remaining'], 'Retry Failed Pages': ['design_remaining'],
  'Generate Architecture': ['architecture', 'pm'], 'Generate Task Plan': ['pm'],
};
const agentStages: Record<string, Stage[]> = {
  plan_all: ['srd', 'user_flows'], all: ['srd', 'user_flows'],
  'Tech Lead Agent': ['srd', 'user_flows'], 'System Requirements Agent': ['srd'],
  'User Flow Planner': ['user_flows'], 'Design Agent': ['design_first_page', 'design_remaining'],
  'System Architect': ['architecture'], 'Project Manager': ['pm'],
};

export interface PlanningActionScope {
  projectId?: string | null;
  entryId?: string | null;
  phase?: string | null;
  artifactType?: string | null;
  buttons?: readonly SupervisorButton[] | null;
  current?: boolean;
  followActive?: boolean;
  /** Render only a durable task phase owned by this message; never infer current work. */
  historyOnly?: boolean;
}

export interface PlanningActionTaskRow {
  id: string;
  title: string;
  task?: Task;
  snapshotAt?: string;
}

/** Retry offers refer to the existing attempt, rather than proposing a new plan. */
export const isPlanningRetryScope = (scope: PlanningActionScope): boolean => Boolean(
  scope.buttons?.some((button) => /^retry\b/i.test(button.label)),
);

const taskStage = (task: Task): Stage | undefined => (
  stageOrder.find((stage) => stage === task.planning_metadata?.stage)
);
const active = (task: Task): boolean => ['queued', 'ai_in_progress', 'human_in_progress'].includes(task.status);
const linkedToEntry = (task: Task, entryId?: string | null): boolean => {
  if (!entryId) return false;
  const metadata = task.planning_metadata;
  const entries = metadata?.trigger_entry_ids;

  return metadata?.trigger_message_id === entryId || metadata?.trigger_entry_id === entryId
    || (Array.isArray(entries) && entries.includes(entryId));
};
const latestRun = (tasks: Task[]): string | undefined => {
  const started = new Map<string, string>();

  tasks.forEach((task) => {
    const runId = task.planning_metadata?.run_id;

    if (runId && (!started.has(runId) || task.created_at < started.get(runId)!)) {
      started.set(runId, task.created_at);
    }
  });

  return Array.from(started).sort(([leftId, left], [rightId, right]) => right.localeCompare(left)
    || rightId.localeCompare(leftId))[0]?.[0];
};

// eslint-disable-next-line max-statements
const buttonStages = (button: SupervisorButton, phase?: string | null): Stage[] => {
  if (button.action === 'run_artifact_updates') return ['artifact_updates'];
  if (button.action === 'trigger_agents') {
    const agents = button.payload?.agents;

    if (!Array.isArray(agents)) return [];
    if (agents.some((agent) => ['plan_all', 'all', 'Tech Lead Agent'].includes(agent))) return ['srd', 'user_flows'];

    return agents.flatMap((agent) => typeof agent === 'string' ? agentStages[agent] ?? [] : []);
  }
  if (button.action !== 'resume_design' && !['continue', 'credit_continue'].includes(button.key)) return [];
  const resumePhase = typeof button.payload?.phase === 'string' ? button.payload.phase : phase;

  return labelStages[button.label] ?? phaseStages[resumePhase ?? ''] ?? [];
};

/** An update offer belongs to one artifact change, even when several share a planning run. */
export const planningPropagationId = (scope: PlanningActionScope): string | undefined => {
  const value = scope.buttons?.find((button) => button.action === 'run_artifact_updates')?.payload?.propagation_id;

  return typeof value === 'string' && value ? value : undefined;
};

const propagationRows = (tasks: Task[], scope: PlanningActionScope): PlanningActionTaskRow[] => {
  const propagationId = planningPropagationId(scope);

  if (!propagationId) return [];

  return orderedPlanningTasks(tasks.filter((task) => task.planning_metadata?.stage === 'artifact_updates'
    && task.planning_metadata?.artifact_type === 'propagation_update'
    && task.planning_metadata?.propagation_id === propagationId))
    .map((task) => ({ id: task.id, title: task.title, task }));
};

// eslint-disable-next-line max-statements
export const planningActionStages = (scope: PlanningActionScope): Stage[] => {
  const buttons = scope.buttons ?? [];
  const stages = buttons.flatMap((button) => buttonStages(button, scope.phase));

  if (stages.length) return stageOrder.filter((stage) => stages.includes(stage));
  if (buttons.length && (!scope.artifactType || buttons.some((button) => (
    ['trigger_agents', 'start_build', 'trigger_stage_deploy'].includes(button.action)
  )))) return [];
  if (phaseStages[scope.phase ?? '']) return phaseStages[scope.phase!];
  const artifact = scope.artifactType?.toLowerCase() ?? '';

  if (['srd', 'requirements'].includes(artifact)) return ['user_flows'];
  if (['architecture', 'architecture_diagram'].includes(artifact)) return ['pm'];
  if (['user_flow', 'user_flows'].includes(artifact)) return ['design_first_page'];

  return [];
};

/** Resolve one action's work, without borrowing unrelated runs or mutating tasks. */
// eslint-disable-next-line max-statements
export const planningActionTaskRows = (allTasks: Task[], scope: PlanningActionScope): PlanningActionTaskRow[] => {
  const projectTasks = allTasks.filter((task) => task.task_type === 'planning' && task.project_id === scope.projectId);

  if (scope.phase === 'artifact_updates' || planningPropagationId(scope)) return propagationRows(projectTasks, scope);
  const tasks = projectTasks.filter((task) => task.planning_metadata?.stage !== 'artifact_updates');
  const linked = tasks.filter((task) => linkedToEntry(task, scope.entryId));
  let runId = latestRun(linked);
  let stages = planningActionStages(scope);
  const hasNewRunButton = scope.buttons?.some((button) => button.action === 'trigger_agents')
    && !isPlanningRetryScope(scope);
  const hasResumeButton = scope.buttons?.some((button) => (
    button.action === 'resume_design' || ['continue', 'credit_continue'].includes(button.key)
  ));

  if (!runId && scope.followActive) {
    runId = latestRun(tasks.filter(active));
    stages = stageOrder.filter((stage) => tasks.some((task) => (
      task.planning_metadata?.run_id === runId && taskStage(task) === stage && active(task)
    )));
  } else if (!runId && scope.current && !hasNewRunButton && (stages.length || hasResumeButton)) {
    runId = latestRun(tasks);
  }
  const runTasks = runId ? tasks.filter((task) => task.planning_metadata?.run_id === runId) : [];
  const latestRetryId = [...runTasks].filter((task) => task.planning_metadata?.artifact_type === 'retry_attempt')
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]?.id;

  if (!stages.length && runId) {
    const source = linked.length ? linked : runTasks.filter((task) => (
      task.planning_metadata?.artifact_type !== 'retry_attempt' && task.status !== 'done' && task.status !== 'cancelled'
    ));
    const candidates = stageOrder.filter((stage) => source.some((task) => taskStage(task) === stage));

    stages = linked.length || scope.followActive ? candidates : candidates.slice(0, 1);
  }
  if (!runId && !scope.current) return [];
  if (!runId && scope.followActive) return [];

  return stages.flatMap((stage) => {
    const rows = orderedPlanningTasks(runTasks.filter((task) => taskStage(task) === stage
      && (task.planning_metadata?.artifact_type !== 'retry_attempt'
        || linkedToEntry(task, scope.entryId) || (scope.followActive && active(task))
        || (scope.current && !scope.entryId && task.id === latestRetryId))));

    if (rows.length) return rows.map((task) => ({ id: task.id, title: task.title, task }));

    return scope.current ? [{ id: `planned:${stage}`, title: STAGES[stage] }] : [];
  });
};
